import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import {
    VStack,
    HStack,
    Text,
    Button,
    Checkbox,
    Box,
    Divider,
    Badge,
} from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { useWorksharingStore } from '../../state/useWorksharingStore';
import { ManageDialogFrame, ManageDialogPrimaryButton } from '../manage/ManageDialogFrame';
import { notify } from '../../utils/notify';

/**
 * Publish Settings dialog (Collaborate > Communicate > Publish Settings).
 * BIM's "Publish Settings" page: choose which sheets, views and 3D
 * views are published to the cloud for browser-based review, then publish.
 */
export function PublishSettingsDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.collabDialog === 'publishSettings');
    const onClose = useAppStore((state) => state.closeCollabDialog);

    const targets = useWorksharingStore((s) => s.publishTargets);
    const setPublishTargets = useWorksharingStore((s) => s.setPublishTargets);
    const togglePublishTarget = useWorksharingStore((s) => s.togglePublishTarget);
    const cloudModels = useWorksharingStore((s) => s.cloudModels);
    const worksharingEnabled = useWorksharingStore((s) => s.worksharingEnabled);
    const [busy, setBusy] = useState(false);

    // Rebuild the sheet/view list whenever the dialog opens (derived state).
    useEffect(() => {
        if (isOpen) setPublishTargets();
    }, [isOpen, setPublishTargets]);

    const sheets = targets.filter((t) => t.kind === 'sheet');
    const views = targets.filter((t) => t.kind === 'view');
    const selectedCount = targets.filter((t) => t.published).length;

    const handlePublish = () => {
        if (!worksharingEnabled) {
            notify('Enable collaboration first — publishing needs a cloud central model.', 'warning');
            return;
        }
        setBusy(true);
        // Simulated publish round-trip; the cloud model version bump happens
        // on the next Synchronize with Central.
        window.setTimeout(() => {
            setBusy(false);
            notify(`Published ${selectedCount} item(s) to the cloud for review.`, 'success');
            onClose();
        }, 700);
    };

    const renderRow = (id: string, name: string, published: boolean) => (
        <HStack
            key={id}
            px={2}
            py={1}
            spacing={2}
            borderBottom="1px solid var(--color-border)"
            _hover={{ bg: 'var(--color-bg-tertiary)' }}
        >
            <Checkbox
                size="sm"
                isChecked={published}
                onChange={() => togglePublishTarget(id)}
            />
            <Text fontSize="sm" isTruncated>{name}</Text>
        </HStack>); return (<ManageDialogFrame
            title={t('collab.publishSettings')}
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            footer={
                <HStack spacing={2}>
                    <ManageDialogPrimaryButton
                        label={busy ? 'Publishing...' : 'Publish'}
                        onClick={handlePublish}
                        isDisabled={busy || selectedCount === 0}
                    />
                    <Button size="sm" variant="ghost" onClick={onClose}>{t('collab.cancel')}</Button>
                </HStack>
            }
        >
            <VStack spacing={3} align="stretch">
                <HStack>
                    <Text fontSize="xs" color="var(--color-text-secondary)">{t('collab.selectTheSheetsAndViewsToMakeAvailableInTheProjectWebViewer')}</Text>
                    {cloudModels[0] && (
                        <Badge colorScheme="blue" flexShrink={0}>{cloudModels[0].hub}</Badge>
                    )}
                </HStack>

                <Box
                    borderWidth="1px"
                    borderColor="var(--color-border)"
                    borderRadius="var(--cad-radius-sm)"
                    maxH="340px"
                    overflowY="auto"
                >
                    <Box px={2} py={1.5} bg="var(--color-bg-tertiary)" borderBottom="1px solid var(--color-border)">
                        <Text fontSize="xs" fontWeight="semibold">Sheets ({sheets.length})</Text>
                    </Box>
                    {sheets.length === 0 ? (
                        <Text fontSize="xs" color="var(--color-text-secondary)" p={2}>{t('collab.noSheetsInThisProject')}</Text>
                    ) : (
                        sheets.map((t) => renderRow(t.id, t.name, t.published))
                    )}
                    <Divider />
                    <Box px={2} py={1.5} bg="var(--color-bg-tertiary)" borderBottom="1px solid var(--color-border)">
                        <Text fontSize="xs" fontWeight="semibold">3D Views ({views.length})</Text>
                    </Box>
                    {views.length === 0 ? (
                        <Text fontSize="xs" color="var(--color-text-secondary)" p={2}>{t('collab.no3DViewsInThisProject')}</Text>
                    ) : (
                        views.map((t) => renderRow(t.id, t.name, t.published))
                    )}
                </Box>
            </VStack>
        </ManageDialogFrame>
    );
}
